import React, { useState } from 'react';
import { Check, X, Loader, AlertCircle, Save, Mail } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const EmailWizard = ({ onClose, onSave }) => {
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    use_ssl: true,
    from_email: '',
    from_name: 'SYLINK Security',
    recipient_email: '',
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // First save config to backend
      const saveResponse = await fetch(`${API_BASE}/api/v1/system/integrations/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          config: config,
          enabled: false
        }),
      });

      if (!saveResponse.ok) {
        const error = await saveResponse.json();
        throw new Error(error.detail || 'Erreur lors de la sauvegarde');
      }

      // Then test via backend API
      const response = await fetch(`${API_BASE}/api/v1/system/integrations/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setTestResult({
          success: true,
          message: result.message || 'Test email sent successfully!'
        });
      } else {
        setTestResult({ success: false, message: result.error || 'Send failed' });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/system/integrations/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          config: config,
          enabled: true
        }),
      });

      if (response.ok) {
        onSave('email', config);
        onClose();
      } else {
        const error = await response.json();
        setTestResult({ success: false, message: error.detail || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = config.smtp_host && config.smtp_port && config.smtp_username &&
                      config.smtp_password && config.from_email && config.recipient_email;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📧</span>
            <div>
              <h2 className="text-2xl font-bold text-brand-green">Configuration Email SMTP</h2>
              <p className="text-sm text-gray-400">Notifications par email via SMTP</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* SMTP Server */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Serveur SMTP *
              </label>
              <input
                type="text"
                value={config.smtp_host}
                onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                placeholder="smtp.exemple.com"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Port *
              </label>
              <input
                type="text"
                value={config.smtp_port}
                onChange={(e) => setConfig({ ...config, smtp_port: e.target.value })}
                placeholder="587"
                className="input-field w-full"
              />
            </div>
          </div>

          {/* SMTP Credentials */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Utilisateur SMTP *
            </label>
            <input
              type="text"
              value={config.smtp_username}
              onChange={(e) => setConfig({ ...config, smtp_username: e.target.value })}
              placeholder="utilisateur@exemple.fr"
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Mot de passe SMTP *
            </label>
            <input
              type="password"
              value={config.smtp_password}
              onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
              placeholder="••••••••"
              className="input-field w-full"
            />
          </div>

          {/* SSL Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="use_ssl"
              checked={config.use_ssl}
              onChange={(e) => setConfig({ ...config, use_ssl: e.target.checked })}
              className="w-4 h-4 accent-brand-green"
            />
            <label htmlFor="use_ssl" className="text-sm text-gray-300">
              Utiliser SSL/TLS (STARTTLS)
            </label>
          </div>

          {/* From Email */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Sender email *
              </label>
              <input
                type="email"
                value={config.from_email}
                onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
                placeholder="noreply@exemple.fr"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Sender name
              </label>
              <input
                type="text"
                value={config.from_name}
                onChange={(e) => setConfig({ ...config, from_name: e.target.value })}
                placeholder="SYLINK Security"
                className="input-field w-full"
              />
            </div>
          </div>

          {/* Recipient Email */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Email destinataire (pour test) *
            </label>
            <input
              type="email"
              value={config.recipient_email}
              onChange={(e) => setConfig({ ...config, recipient_email: e.target.value })}
              placeholder="admin@exemple.fr"
              className="input-field w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              The test email will be sent to this address
            </p>
          </div>

          {/* Test Button */}
          <div className="pt-4 border-t border-gray-800">
            <button
              onClick={handleTest}
              disabled={testing || !isFormValid}
              className="btn-green w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {testing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  <span>Envoyer un email de test</span>
                </>
              )}
            </button>

            {testResult && (
              <div className={`mt-4 p-4 rounded-lg border flex items-start gap-3 ${
                testResult.success
                  ? 'bg-brand-green/10 border-brand-green/50'
                  : 'bg-red-500/10 border-red-500/50'
              }`}>
                {testResult.success ? (
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                )}
                <div>
                  <div className={`font-medium ${testResult.success ? 'text-brand-green' : 'text-red-400'}`}>
                    {testResult.success ? 'Success' : 'Failure'}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">{testResult.message}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!testResult?.success || saving}
            className="btn-green disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Sauvegarde...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Sauvegarder & Activer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailWizard;
